import { useKeycloak } from '@react-keycloak/web';
import { useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { Spaces } from '@/api/Spaces';
import {
  KioskResponse,
  MultipleSpacesAvailabilityRequest,
  PageResponseSpaceResponse,
  SpaceFilterResponse,
  SpaceRequestDataWithFiles,
  SpaceResponse,
  SpaceSupportPictureResponse,
  SpaceTimelineRequest,
  SpaceTimelineResponse,
  SpacesRequest,
  SupportPictureResponse,
} from '@/api/data-contracts';
import { queryKeys } from '@/react-query/constants';
import useUploadProgressService from '@/shared/api/uploadProgress.service';

const useAdvertisingSpacesService = () => {
  const { keycloak } = useKeycloak();

  const queryClient = useQueryClient();
  const advertisingSpacesApi = useUploadProgressService(Spaces);

  const getAdvertisingSpaces = useCallback(
    async (
      query: {
        request: SpacesRequest;
      },
      signal: AbortSignal | undefined,
    ): Promise<PageResponseSpaceResponse> => {
      const availabilities: any[] | undefined = queryClient.getQueryData([
        queryKeys.spacesAvailability,
      ]);
      const { data } = await advertisingSpacesApi.listSpaces(query.request, {
        signal,
      });

      return {
        totalElements: data.totalElements,
        content: data.content.map((item: SpaceResponse) => {
          const itemAvailability = availabilities?.find((a) => a.id === item.id);
          return {
            ...item,
            status: itemAvailability ? 'AVAILABLE' : item.status,
            availability: {
              start: itemAvailability?.start || item.availability.start,
              end: itemAvailability?.end || item.availability.end,
            },
          };
        }),
      };
    },
    [advertisingSpacesApi, queryClient],
  );

  /**
   * Occupancy for the timeline widget. Separate from the spaces list on purpose: the timeline window
   * spans up to two years regardless of the campaign-period filter, and another tenant's bookings
   * come back anonymised — a period and nothing else — which the list endpoint cannot express.
   */
  const getSpacesTimeline = useCallback(
    async (request: SpaceTimelineRequest, signal?: AbortSignal): Promise<SpaceTimelineResponse> => {
      const { data } = await advertisingSpacesApi.getSpacesTimeline(request, { signal });
      return data;
    },
    [advertisingSpacesApi],
  );

  const getSpacesFilters = useCallback(
    async (request: Partial<SpacesRequest>, signal?: AbortSignal): Promise<SpaceFilterResponse> => {
      const { data } = await advertisingSpacesApi.getSpacesFilters(request, { signal });
      return data;
    },
    [advertisingSpacesApi],
  );

  const getAdvertisingSpacesForLocation = async (
    request: SpacesRequest,
    signal: AbortSignal | undefined,
  ): Promise<SpaceResponse[]> => {
    const { data } = await advertisingSpacesApi.listSpaces(request, {
      signal,
    });

    return data?.content || [];
  };

  const getAdvertisingSpaceDetails = async (
    id: string,
    query?: {
      start?: string;
      end?: string;
    },
    signal?: AbortSignal | undefined,
  ): Promise<SpaceResponse> => {
    const { data } = await advertisingSpacesApi.showSpace(id, query, { signal });
    return data;
  };

  const createAdvertisingSpace = async (
    form: SpaceRequestDataWithFiles,
  ): Promise<SpaceResponse> => {
    const { data } = await advertisingSpacesApi.addSpace(form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  };

  const addSpaceDraft = async (form: SpaceRequestDataWithFiles): Promise<SpaceResponse> => {
    const { data } = await advertisingSpacesApi.addSpaceDraft(form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  };

  const updateAdvertisingSpace = async (
    spaceId: string,
    form: SpaceRequestDataWithFiles,
  ): Promise<SpaceResponse> => {
    const { data } = await advertisingSpacesApi.updateSpace(spaceId, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  };

  const updateDraft = async (
    spaceId: string,
    form: SpaceRequestDataWithFiles,
  ): Promise<SpaceResponse> => {
    const { data } = await advertisingSpacesApi.updateSpaceDraft(spaceId, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  };

  const deleteAdvertisingSpace = async (spaceId: string): Promise<void> => {
    await advertisingSpacesApi.deleteSpace(spaceId);
  };

  const unpublishSpace = async (spaceId: string): Promise<SpaceResponse> => {
    const { data } = await advertisingSpacesApi.unpublishSpace(spaceId);
    return data;
  };

  const downloadSpaceDocumentById = useCallback(
    async (spaceId: string, documentId: string, signal?: AbortSignal): Promise<Blob> => {
      const { data } = await advertisingSpacesApi.downloadSpaceDocument1(spaceId, documentId, {
        format: 'blob',
        signal,
      });
      return data;
    },
    [advertisingSpacesApi],
  );

  const downloadSpaceThumbnail = useCallback(
    async (spaceId: string, documentId: string, signal?: AbortSignal): Promise<Blob> => {
      const { data } = await advertisingSpacesApi.downloadSpaceDocument(spaceId, documentId, {
        format: 'blob',
        signal,
      });
      return data;
    },
    [advertisingSpacesApi],
  );

  const deleteSpaceDocument = async (
    spaceId: string,
    documentId: string,
  ): Promise<SpaceResponse> => {
    const { data } = await advertisingSpacesApi.deleteSpaceDocument(spaceId, documentId);
    return data;
  };

  const getSpaceSupportPictures = async (spaceId: string): Promise<SupportPictureResponse> => {
    const { data } = await advertisingSpacesApi.getSupportPictures(spaceId);
    return data;
  };

  const downloadSpaceSupportPicture = useCallback(
    async (spaceId: string, documentId: string, signal?: AbortSignal): Promise<Blob> => {
      const { data } = await advertisingSpacesApi.downloadSupportPictures(spaceId, documentId, {
        format: 'blob',
        signal,
      });
      return data;
    },
    [advertisingSpacesApi],
  );

  const getSpaceAvailabilityResource = useCallback(
    async (spaceId: string, signal?: AbortSignal | undefined): Promise<string[]> => {
      const { data } = await advertisingSpacesApi.getSpaceAvailabilityResource(spaceId, { signal });
      return data;
    },
    [advertisingSpacesApi],
  );

  const getMultipleSpacesAvailabilityResource = useCallback(
    async (
      requestData: MultipleSpacesAvailabilityRequest,
      signal?: AbortSignal | undefined,
    ): Promise<string[]> => {
      const { data } = await advertisingSpacesApi.getMultipleSpacesAvailabilityResource(
        requestData,
        { signal },
      );
      return data;
    },
    [advertisingSpacesApi],
  );

  const getSpaceGeometry = useCallback(
    async (spaceId: string, signal?: AbortSignal): Promise<string | null> => {
      const { data } = await advertisingSpacesApi.getSpaceGeometry(spaceId, { signal });
      return data ?? null;
    },
    [advertisingSpacesApi],
  );

  const updateSpaceGeometry = useCallback(
    async (spaceId: string, geometry: string | null): Promise<KioskResponse> => {
      const { data } = await advertisingSpacesApi.updateSpaceGeometry(spaceId, geometry);
      return data;
    },
    [advertisingSpacesApi],
  );

  // Artwork dropzones, shared by every booking of the space. Deliberately a different slot from
  // `geometry` above, which holds the floor-plan pin — the two shapes are incompatible.
  const getSpaceArtworkGeometry = useCallback(
    async (spaceId: string, signal?: AbortSignal): Promise<string | null> => {
      const { data } = await advertisingSpacesApi.getSpaceArtworkGeometry(spaceId, { signal });
      return data ?? null;
    },
    [advertisingSpacesApi],
  );

  const updateSpaceArtworkGeometry = useCallback(
    async (spaceId: string, geometry: string | null): Promise<string | null> => {
      const { data } = await advertisingSpacesApi.updateSpaceArtworkGeometry(spaceId, geometry);
      return data ?? null;
    },
    [advertisingSpacesApi],
  );

  const getSpaceSupportNamePictures = useCallback(
    async (signal?: AbortSignal | undefined): Promise<SpaceSupportPictureResponse[]> => {
      const { data } = await advertisingSpacesApi.getSpaceSupportNamePictures({ signal });
      return data;
    },
    [advertisingSpacesApi],
  );

  const downloadIndividualSupportNamePicture = useCallback(
    async (url: string, signal?: AbortSignal) => {
      return axios.get(`${import.meta.env.VITE_API_URL}${url}`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${keycloak.token}` },
        signal,
      });
    },
    [keycloak.token],
  );

  const getSpacesCount = async (request: SpacesRequest, signal?: AbortSignal) => {
    const response = await advertisingSpacesApi.countSpaces({ request }, { signal });
    return response.data;
  };

  const downloadSpaceLayoutSketch = useCallback(
    async (spaceId: string, documentId: string, signal?: AbortSignal): Promise<Blob> => {
      const { data } = await advertisingSpacesApi.downloadSpaceLayoutSketches(spaceId, documentId, {
        format: 'blob',
        signal,
      });
      return data;
    },
    [advertisingSpacesApi],
  );

  return {
    getSpacesCount,
    getAdvertisingSpaces,
    getAdvertisingSpacesForLocation,
    getAdvertisingSpaceDetails,
    createAdvertisingSpace,
    updateAdvertisingSpace,
    deleteAdvertisingSpace,
    unpublishSpace,
    downloadSpaceDocumentById,
    deleteSpaceDocument,
    getSpaceAvailabilityResource,
    addSpaceDraft,
    updateDraft,
    getSpaceGeometry,
    updateSpaceGeometry,
    getSpaceArtworkGeometry,
    updateSpaceArtworkGeometry,
    getSpaceSupportNamePictures,
    downloadIndividualSupportNamePicture,
    getSpacesFilters,
    getSpacesTimeline,
    downloadSpaceThumbnail,
    downloadSpaceLayoutSketch,
    getMultipleSpacesAvailabilityResource,
    getSpaceSupportPictures,
    downloadSpaceSupportPicture,
  };
};

export default useAdvertisingSpacesService;
